import { pick } from "es-toolkit";
import { toJS } from "mobx";

import { getRandomTailwindColor } from "@/utils/tailwindColors";

import type { DocumentModel } from "@/models/DocumentModel";
import type { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import type { ConstantNodeModel } from "@/models/nodes/ConstantNodeModel";
import type { NodeProps } from "@/models/nodes/types";
import type { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import type { NodeJson } from "@corca/data";

type MergePropsOptions<T extends BaseNodeModel<P>, P extends NodeProps> = {
  node: T | undefined;
  props: Partial<P>;
  keys?: (keyof P)[];
};

export function mergePropsFromNode<T extends BaseNodeModel<P>, P extends NodeProps = T["props"]>(
  { node, props, keys = [] }: MergePropsOptions<T, P>,
  merge: (mergedProps: Partial<P>) => P,
): P {
  if (!node) {
    return merge(props);
  }

  const existingProps = toJS(node.props);
  const pickedProps = keys.length > 0 ? pick(existingProps, keys) : existingProps;

  return merge({ ...props, ...pickedProps });
}

export type CopyNodePropsOptions = {
  /**
   * Which document nodes may be copied from; a field replacing a node leaves that node out, so
   * the chosen entry keeps its own name rather than the replaced node's.
   */
  copyFrom?: (node: VariableNodeModel | ConstantNodeModel) => boolean;
};

export function copyNodePropsFromDoc(
  doc: DocumentModel,
  nodeJson: NodeJson,
  options: CopyNodePropsOptions = {},
): NodeJson {
  const props = getNodeProps(doc, nodeJson, options);
  const children = nodeJson.children?.map((child) => copyNodePropsFromDoc(doc, child, options));

  return {
    ...nodeJson,
    props,
    children,
  } as NodeJson;
}

function getNodeProps(
  doc: DocumentModel,
  nodeJson: NodeJson,
  { copyFrom = () => true }: CopyNodePropsOptions,
): NodeJson["props"] {
  switch (nodeJson.type) {
    case "variable":
    case "constant": {
      const symbolValue = getSymbolValue(nodeJson);
      const node = doc
        .getNodesByType(nodeJson.type)
        .find((candidate) => candidate.value === symbolValue && copyFrom(candidate));

      return mergePropsFromNode(
        {
          node,
          props: nodeJson.props,
          keys: ["name", "color"],
        },
        (mergedProps) => ({
          ...mergedProps,
          color: mergedProps?.color || getRandomTailwindColor(symbolValue, "slate"),
        }),
      );
    }

    default: {
      return nodeJson.props;
    }
  }
}

function getSymbolValue(nodeJson: NodeJson): string | undefined {
  const symbolNodeProps = nodeJson.children?.find((child) => child.type === "symbol");
  return symbolNodeProps?.props.value;
}
